/* RichContent — render plain-text article body with auto-embedded media.
 *
 * Recognised on a line of its own:
 *   • YouTube (watch / youtu.be / shorts / embed)  → 16:9 iframe
 *   • Vimeo                                       → 16:9 iframe
 *   • Other http(s) URLs                          → clickable link (target=_blank)
 *
 * Plain text lines keep their original whitespace via `whitespace-pre-wrap`.
 * Multiple consecutive blank lines collapse to a single paragraph gap.
 */
import React from "react";

const YT_PATTERNS = [
  /^https?:\/\/(?:www\.|m\.)?youtube\.com\/watch\?(?:[^&\s]*&)*v=([\w-]{6,})/i,
  /^https?:\/\/youtu\.be\/([\w-]{6,})/i,
  /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([\w-]{6,})/i,
  /^https?:\/\/(?:www\.)?youtube\.com\/shorts\/([\w-]{6,})/i,
];

const VIMEO_PATTERN = /^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/i;
const URL_PATTERN = /^https?:\/\/[^\s]+$/i;

function detectMedia(line) {
  const trimmed = (line || "").trim();
  if (!trimmed) return null;
  for (const re of YT_PATTERNS) {
    const m = trimmed.match(re);
    if (m) return { kind: "youtube", id: m[1] };
  }
  const v = trimmed.match(VIMEO_PATTERN);
  if (v) return { kind: "vimeo", id: v[1] };
  if (URL_PATTERN.test(trimmed)) return { kind: "link", url: trimmed };
  return null;
}

function VideoFrame({ src, title }) {
  return (
    <div
      className="my-5 w-full rounded-lg overflow-hidden bg-black shadow-sm border border-gray-100"
      style={{ aspectRatio: "16/9" }}
      data-testid="content-video-embed"
    >
      <iframe
        src={src}
        title={title || "Video"}
        className="w-full h-full"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
}

export default function RichContent({ text, className = "" }) {
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);

  // Group consecutive plain lines into paragraphs so blank lines become breaks
  // naturally; video lines become standalone blocks.
  const blocks = [];
  let buf = [];
  const flushBuf = () => {
    if (!buf.length) return;
    blocks.push({ kind: "text", text: buf.join("\n") });
    buf = [];
  };

  for (const line of lines) {
    const m = detectMedia(line);
    if (m && (m.kind === "youtube" || m.kind === "vimeo")) {
      flushBuf();
      blocks.push(m);
    } else {
      buf.push(line);
    }
  }
  flushBuf();

  return (
    <div className={className}>
      {blocks.map((b, i) => {
        if (b.kind === "youtube") {
          return (
            <VideoFrame
              key={i}
              src={`https://www.youtube.com/embed/${b.id}`}
              title="YouTube videosu"
            />
          );
        }
        if (b.kind === "vimeo") {
          return (
            <VideoFrame
              key={i}
              src={`https://player.vimeo.com/video/${b.id}`}
              title="Vimeo videosu"
            />
          );
        }
        return (
          <p key={i} className="whitespace-pre-wrap leading-relaxed">
            {b.text}
          </p>
        );
      })}
    </div>
  );
}
